import { createFileRoute } from '@tanstack/react-router';
import { AlertCircle, CheckCircle2, Clock, Loader2 } from 'lucide-react';
import logo from '@/assets/OKYC logo.webp';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { useDocumentRequests, useValidateUploadToken } from '@/features/upload/api/upload';
import { FileUploadForm } from '@/features/upload/components/file-upload-form';

export const Route = createFileRoute('/upload/$token')({
  component: UploadPage,
});

function UploadPage() {
  const { token } = Route.useParams();

  const {
    data: tokenInfo,
    isLoading: isValidating,
    error: tokenError,
  } = useValidateUploadToken(token);
  const { data: documentRequests, isLoading: isLoadingDocs } = useDocumentRequests(
    token,
    tokenInfo?.valid === true,
  );

  // Show loading state
  if (isValidating) {
    return (
      <Card className="w-full rounded-[5px] border border-[#D4DAE0]">
        <CardHeader className="space-y-4 text-center">
          <img src={logo} alt="OKYC Logo" className="mx-auto h-16 w-auto" />
        </CardHeader>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
          <p className="text-[16px] text-[#051923]">Validating your upload link...</p>
        </CardContent>
      </Card>
    );
  }

  // Handle invalid/expired token
  if (tokenError || !tokenInfo?.valid) {
    return (
      <Card className="w-full rounded-[5px] border border-[#D4DAE0]">
        <CardHeader className="space-y-4 text-center">
          <img src={logo} alt="OKYC Logo" className="mx-auto h-16 w-auto" />
          <div className="mx-auto h-12 w-12 rounded-full bg-destructive/10 flex items-center justify-center">
            <AlertCircle className="h-6 w-6 text-destructive" />
          </div>
          <div className="space-y-2">
            <h1 className="text-[20px] font-semibold leading-[120%] text-destructive">
              Link Invalid or Expired
            </h1>
            <p className="text-[16px] font-normal leading-[22px] text-[#051923]">
              {tokenInfo?.error || tokenError?.message || 'This upload link is no longer valid.'}
            </p>
          </div>
        </CardHeader>
        <CardContent className="text-center">
          <p className="text-[14px] text-[#051923]">
            Please contact the person who sent you this link to request a new one.
          </p>
        </CardContent>
      </Card>
    );
  }

  // Handle upload limit reached
  if (tokenInfo.remainingUploads === 0) {
    return (
      <Card className="w-full rounded-[5px] border border-[#D4DAE0]">
        <CardHeader className="space-y-4 text-center">
          <img src={logo} alt="OKYC Logo" className="mx-auto h-16 w-auto" />
          <div className="mx-auto h-12 w-12 rounded-full bg-amber-500/10 flex items-center justify-center">
            <CheckCircle2 className="h-6 w-6 text-amber-500" />
          </div>
          <div className="space-y-2">
            <h1 className="text-[20px] font-semibold leading-[120%] text-[#051923]">
              Upload Limit Reached
            </h1>
            <p className="text-[16px] font-normal leading-[22px] text-[#051923]">
              You have reached the maximum number of uploads for this link.
            </p>
          </div>
        </CardHeader>
        <CardContent className="text-center">
          <p className="text-[14px] text-[#051923]">
            If you need to upload more documents, please contact the requester.
          </p>
        </CardContent>
      </Card>
    );
  }

  const handleUploadSuccess = () => {
    window.location.href = '/upload/success';
  };

  // Check if all documents have been received
  const allReceived = documentRequests?.length && documentRequests.every((doc) => doc.received);

  return (
    <Card className="w-full rounded-[5px] border border-[#D4DAE0]">
      <CardHeader className="space-y-4 text-center">
        <img src={logo} alt="OKYC Logo" className="mx-auto h-16 w-auto" />

        <div className="space-y-2">
          <h1 className="text-[20px] font-semibold leading-[120%] text-[#051923]">
            Document Upload
          </h1>
          <p className="text-[16px] font-normal leading-[22px] text-[#051923]">
            Please upload the requested documents for your KYC assessment.
          </p>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-[14px] text-[#051923]">
          {tokenInfo.clientName && (
            <span>
              <strong>Client:</strong> {tokenInfo.clientName}
            </span>
          )}
          {tokenInfo.assessmentRef && (
            <span>
              <strong>Reference:</strong> {tokenInfo.assessmentRef}
            </span>
          )}
          <span className="flex items-center gap-1">
            <Clock className="h-4 w-4" />
            {tokenInfo.remainingUploads} upload{tokenInfo.remainingUploads !== 1 ? 's' : ''}{' '}
            remaining
          </span>
        </div>
      </CardHeader>

      <CardContent>
        {isLoadingDocs ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : allReceived ? (
          <div className="text-center py-12">
            <div className="h-16 w-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="h-8 w-8 text-green-600" />
            </div>
            <p className="text-[18px] font-medium text-green-700">
              All documents have been received!
            </p>
            <p className="text-[14px] text-[#051923] mt-2">
              Thank you for uploading your documents.
            </p>
          </div>
        ) : (
          <FileUploadForm
            token={token}
            documentRequests={documentRequests ?? []}
            onSuccess={handleUploadSuccess}
          />
        )}
      </CardContent>
    </Card>
  );
}
